import React, { useState, useEffect, useRef } from 'react';
import { clientsService } from '../services/clientsService';
import { toast } from '../utils/toast';
import PullToRefresh from '../components/PullToRefresh';
import syncService from '../services/syncService';
import { useSettings } from '../services/SettingsContext';
import { normalizedProfileFields, phoneHref } from '../utils/profileFields';
import './Clients.css';

function parseCsv(text) {
  const rows = [];
  let row = [];
  let value = '';
  let quoted = false;
  const source = String(text || '').replace(/^\uFEFF/, '');
  for (let i = 0; i < source.length; i += 1) {
    const char = source[i];
    if (char === '"' && quoted && source[i + 1] === '"') { value += '"'; i += 1; }
    else if (char === '"') quoted = !quoted;
    else if (char === ',' && !quoted) { row.push(value); value = ''; }
    else if ((char === '\n' || char === '\r') && !quoted) {
      if (char === '\r' && source[i + 1] === '\n') i += 1;
      row.push(value); value = '';
      if (row.some(cell => cell.trim())) rows.push(row);
      row = [];
    } else value += char;
  }
  row.push(value);
  if (row.some(cell => cell.trim())) rows.push(row);
  if (rows.length < 2) return [];
  const aliases = { имя: 'name', name: 'name', email: 'email', почта: 'email', телефон: 'phone', phone: 'phone', компания: 'company', company: 'company', заметки: 'notes', notes: 'notes', рейтинг: 'rating', rating: 'rating' };
  const headers = rows[0].map(cell => aliases[cell.trim().toLowerCase()] || cell.trim().toLowerCase());
  return rows.slice(1).map(cells => Object.fromEntries(headers.map((header, index) => [header, cells[index] || ''])));
}

const Clients = () => {
  const { settings } = useSettings();
  const visibleFields = normalizedProfileFields(settings, 'client_fields');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    notes: '',
    rating: 0,
  });
  const importInputRef = useRef(null);

  useEffect(() => {
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const loadClients = async () => {
    try {
      const response = await clientsService.getAll({ search });

      let list = [];
      if (response) {
        if (Array.isArray(response)) list = response;
        else if (response.data) list = Array.isArray(response.data) ? response.data : [];
      }
      setClients(list);
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error('Ошибка загрузки клиентов');
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await clientsService.update(editingClient.id, formData);
        toast.success('Клиент обновлен');
      } else {
        await clientsService.create(formData);
        toast.success('Клиент создан');
      }
      setShowModal(false);
      setEditingClient(null);
      resetForm();
      loadClients();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Ошибка при сохранении клиента');
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      email: client.email || '',
      phone: client.phone || '',
      company: client.company || '',
      notes: client.notes || '',
      rating: client.rating || 0,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Удалить клиента?')) {
      try {
        await clientsService.delete(id);
        toast.success('Клиент удален');
        loadClients();
      } catch (error) {
        toast.error('Ошибка при удалении клиента');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      notes: '',
      rating: 0,
    });
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await syncService.syncAll();
      await loadClients();
    } catch (error) {
      console.error('Error refreshing clients:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleExport = async () => {
    try {
      const blob = await clientsService.exportCsv();
      const href = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = href;
      link.download = 'savelev-clients.csv';
      link.click();
      URL.revokeObjectURL(href);
    } catch (_) { toast.error('Не удалось выгрузить клиентов'); }
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const parsed = parseCsv(await file.text());
      if (!parsed.length) return toast.error('CSV пустой или не распознан');
      const result = await clientsService.importClients(parsed);
      toast.success(`Импорт: создано ${result.created}, обновлено ${result.updated}, пропущено ${result.skipped}`);
      await loadClients();
    } catch (error) { toast.error(error.response?.data?.error || 'Не удалось импортировать CSV'); }
  };

  if (loading) {
    return (
      <div className="clients-page gnom-clients">
        <header className="gnom-clients-header">
          <span className="gnom-clients-header-placeholder" />
          <h1 className="gnom-clients-title">Клиенты</h1>
          <span className="gnom-clients-header-placeholder" />
        </header>
        <div className="gnom-clients-body">
          <div className="spinner gnom-clients-spinner" />
        </div>
      </div>
    );
  }

  return (
    <PullToRefresh onRefresh={handleRefresh} disabled={loading || refreshing}>
      <div className="clients-page gnom-clients">
        <header className="gnom-clients-header">
          <span className="gnom-clients-header-placeholder" />
          <h1 className="gnom-clients-title">Клиенты</h1>
          <button
            type="button"
            className="gnom-clients-add"
            onClick={() => { setEditingClient(null); resetForm(); setShowModal(true); }}
            aria-label="Добавить клиента"
          >
            +
          </button>
        </header>

        <div className="gnom-clients-body">
          <div className="gnom-clients-search">
            <input
              type="text"
              placeholder="Поиск клиентов..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="gnom-clients-search-input"
            />
          </div>
          <div className="gnom-clients-import-actions">
            <button type="button" className="gnom-client-btn gnom-client-btn-edit" onClick={handleExport}>Выгрузить CSV</button>
            <button type="button" className="gnom-client-btn gnom-client-btn-edit" onClick={() => importInputRef.current?.click()}>Импортировать CSV</button>
            <input ref={importInputRef} type="file" accept=".csv,text/csv" hidden onChange={handleImport} />
          </div>

          <div className="gnom-clients-list">
            {clients && clients.length === 0 && (
              <p className="gnom-clients-empty">Нет клиентов. Добавьте первого.</p>
            )}
            {clients && clients.map((client) => (
              <div key={client.id} className="gnom-client-card">
                <div className="gnom-client-card-main">
                  <div className="gnom-client-name-row">
                    <h3 className="gnom-client-name">{client.name}</h3>
                    {visibleFields.rating && <span className="gnom-client-rating">{'⭐'.repeat(client.rating || 0)}</span>}
                  </div>
                  {visibleFields.company && client.company && <p className="gnom-client-company">{client.company}</p>}
                  {visibleFields.email && client.email && <p className="gnom-client-meta">📧 {client.email}</p>}
                  {visibleFields.phone && client.phone && <a className="gnom-client-phone" href={phoneHref(client.phone)}>📱 {client.phone}</a>}
                  {visibleFields.notes && client.notes && <p className="gnom-client-notes">{client.notes}</p>}
                </div>
                <div className="gnom-client-actions">
                  {visibleFields.phone && client.phone && <a className="gnom-client-btn gnom-client-btn-call" href={phoneHref(client.phone)}>Позвонить</a>}
                  <button type="button" className="gnom-client-btn gnom-client-btn-edit" onClick={() => handleEdit(client)}>
                    Изменить
                  </button>
                  <button type="button" className="gnom-client-btn gnom-client-btn-delete" onClick={() => handleDelete(client.id)}>
                    Удалить
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {showModal && (
          <div className="gnom-clients-modal-overlay" onClick={() => { setShowModal(false); setEditingClient(null); resetForm(); }}>
            <div className="gnom-clients-modal" onClick={(e) => e.stopPropagation()}>
              <h2 className="gnom-clients-modal-title">{editingClient ? 'Редактировать клиента' : 'Новый клиент'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="gnom-clients-form-group">
                  <label>Имя *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="gnom-clients-input"
                  />
                </div>
                {visibleFields.email && <div className="gnom-clients-form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="gnom-clients-input"
                  />
                </div>}
                {visibleFields.phone && <div className="gnom-clients-form-group">
                  <label>Телефон</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="gnom-clients-input"
                  />
                </div>}
                {visibleFields.company && <div className="gnom-clients-form-group">
                  <label>Компания</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="gnom-clients-input"
                  />
                </div>}
                {visibleFields.rating && <div className="gnom-clients-form-group">
                  <label>Рейтинг</label>
                  <select
                    value={formData.rating}
                    onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value, 10) })}
                    className="gnom-clients-input"
                  >
                    {[0, 1, 2, 3, 4, 5].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>}
                {visibleFields.notes && <div className="gnom-clients-form-group">
                  <label>Заметки</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="gnom-clients-input gnom-clients-textarea"
                    rows={3}
                  />
                </div>}
                <div className="gnom-clients-modal-actions">
                  <button type="submit" className="gnom-clients-btn gnom-clients-btn-primary">Сохранить</button>
                  <button type="button" onClick={() => { setShowModal(false); setEditingClient(null); resetForm(); }} className="gnom-clients-btn gnom-clients-btn-secondary">Отмена</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </PullToRefresh>
  );
};

export default Clients;
